import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, take } from 'rxjs/operators';

import { AuthService } from '@app/core/services/auth.service';

/**
 * Onboarding-wizard counterpart to `authGuard` — only ANONYMOUS users
 * may enter `/onboarding/*`. Authenticated users are redirected to
 * `/dashboard` (their landing page after login).
 *
 * Mirrors `authGuard`'s page-refresh handling: if `restoreSession()`
 * is in flight (cookie present but `/auth/me` still pending) we wait
 * for it to resolve before deciding. Without that, a logged-in user
 * hitting `/onboarding/account-type` directly on a refresh would slip
 * through during the few ms before `currentUser` is populated.
 */
export const anonymousOnlyGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const redirectToDashboard = () => router.createUrlTree(['/dashboard']);

  if (authService.restoreInProgress()) {
    return toObservable(authService.restoreInProgress).pipe(
      filter((inProgress) => !inProgress),
      take(1),
      map(() => (authService.isAuthenticated() ? redirectToDashboard() : true)),
    );
  }

  return authService.isAuthenticated() ? redirectToDashboard() : true;
};
